<script setup lang="ts">
  import { computed } from 'vue';

  interface Props {
    stage: number;
    current: number;
    title: string;
  }

  const props = defineProps<Props>();

  const active = computed(() => props.stage === props.current);
  const passed = computed(() => props.current > props.stage);
  const keepSpace = computed(() => props.current < props.stage);
</script>
<template>
  <div class="stage" :class="{ active, passed }">
    <div class="stage-header">
      <div class="number">{{ stage }}</div>
      <component :is="active? 'h1' : 'span'" class="title">{{ title }}</component>
    </div>
    <div v-if="active" class="stage-content">
      <slot name="default" />
    </div>
    <div v-if="passed" class="stage-summary">
      <slot name="summary" />
    </div>
    <div v-if="keepSpace" class="stage-summary placeholder" />
  </div>
</template>
<style scoped lang="scss">
  .stage {
    scroll-margin: 3rem;

    .stage-header {
      display: flex;
      align-items: flex-start;
      gap: 1rem;

      .number {
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--secondary-color);
        font-size: 1rem;
        font-weight: var(--font-weight-bold);
        line-height: 1rem;
        border: 1px solid var(--secondary-border-active);
        border-radius: 50%;
        width: 2rem;
        height: 2rem;
      }

      .title {
        padding: 0.5rem 0;
        font-weight: var(--font-weight-semi);
        line-height: 1rem;
        font-size: 1.125rem;
      }
    }

    .stage-content {
      display: flex;
      flex-direction: column;
      gap: 2rem;
      margin: 1rem 0;
      padding: 2rem;
      background: var(--section-background);
      border-inline: var(--section-border-inline);
      border-block: var(--section-border-block);
      border-radius: var(--section-border-radius);
    }

    .stage-summary {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      padding: 0.5rem 1rem 2rem 2rem;
      border-left: 2px dotted var(--secondary-border-hover);
      color: var(--secondary-color);
      margin-left: calc(1rem - 1px);
      font-size: 0.875rem;
      width: auto;

      :deep(b) {
        color: var(--color-default);
      }
    }

    &.active {
      .stage-header {
        .number {
          border-color: var(--primary-border);
        }
      }
    }

    &.passed {
      .stage-header {
        .number {
          background: var(--primary-background);
          border-color: var(--primary-border);
          color: var(--primary-foreground);
        }
      }

      .stage-summary {
        border-color: var(--primary-border);
      }
    }

    &:last-child {
      .stage-summary {
        &.placeholder {
          display: none;
        }
      }
    }


    @media(max-width: 992px) {
      padding: 0 10px;
      .stage-content {
        padding: 1rem 1rem;
      }
    }
  }
</style>
